"use client";

// Pure client-side service (no React hooks here)
import {
  buildSabreDxUrlShared,
  jsonFetch,
  healthCheck as sharedHealthCheck,
  API_MOCK,
} from "../../shared/services/common.client";

import type {
  HealthCheckResponse,
  CallbackErrorCode,
  QualifierSuccessResponse,
  MybQualifierRequestBody as QualifierRequestBody,
} from "@jb-manage-trips/types/pnr";
import type { ManageTripsRuntimeConfig } from "@jb-manage-trips/types/config";

export class QualifierHttpError extends Error {
  code?: CallbackErrorCode;
  status?: number;
  constructor(message: string, code?: CallbackErrorCode, status?: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

type JsonFetchErrorShape = {
  status?: unknown;
  response?: {
    error?: { cbErrCode?: unknown };
    createdBy?: unknown;
  };
};

/** Create the service (pure, testable) */
export function createQualifierService(manageTrips: ManageTripsRuntimeConfig) {
  function buildSabreDxUrl(
    lastName: string,
    pnrRecordLocator: string,
    queryParameters?: Record<string, string | undefined>,
    platformOS?: "iOS" | "Android" | "Other",
  ): string {
    return buildSabreDxUrlShared(
      manageTrips.MANAGE_TRIPS.API.SABRE_DX_URL,
      lastName,
      pnrRecordLocator,
      queryParameters,
      platformOS,
    );
  }

  async function doHealthCheck(
    pnrRecordLocator: string,
    lastName: string,
    signal?: AbortSignal,
  ): Promise<HealthCheckResponse> {
    return sharedHealthCheck(
      manageTrips.MANAGE_TRIPS.API.HEALTH_CHECK_URL,
      { b6PNRRecordLocator: pnrRecordLocator, lastName },
      signal,
    );
  }

  async function mybPnrEmailQualifierService(
    body: QualifierRequestBody,
    signal?: AbortSignal,
  ): Promise<QualifierSuccessResponse> {
    if (API_MOCK) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      return { mybQualifierRS: { message: "ok" } };
    }

    try {
      return await jsonFetch<QualifierSuccessResponse>(
        manageTrips.MANAGE_TRIPS.API.EMAIL_QUALIFIER_URL,
        {
          body,
          signal,
        },
      );
    } catch (error: unknown) {
      const error_ = error as JsonFetchErrorShape;

      const parsed = error_.response;
      const createdBy =
        typeof parsed?.createdBy === "string" ? parsed.createdBy : undefined;
      const rawCallback = parsed?.error?.cbErrCode;
      const callback =
        typeof rawCallback === "string"
          ? (rawCallback as CallbackErrorCode)
          : undefined;

      const code: CallbackErrorCode | undefined =
        callback ??
        (createdBy === "ValidationFilter"
          ? "MYB-QUALIFIER-BAD-REQUEST-400006"
          : undefined);

      const status =
        typeof error_.status === "number" ? error_.status : undefined;
      const message = `HTTP ${String(status ?? 500)}`;

      throw new QualifierHttpError(message, code, status);
    }
  }

  return {
    buildSabreDxUrl,
    doHealthCheck,
    mybPnrEmailQualifierService,

    routes: manageTrips.MANAGE_TRIPS.ROUTES,
  };
}
